// Utility functions for detecting and handling mobile app web views

export interface WebViewInfo {
  isWebView: boolean;
  isIOSWebView: boolean;
  isAndroidWebView: boolean;
  isMobileApp: boolean;
  platform?: string;
}

/**
 * Detects if the current environment is a mobile app web view
 */
export const detectWebView = (): WebViewInfo => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';

  // Check for iOS web view (common patterns)
  const isIOSWebView = /iPhone|iPad|iPod/.test(navigator.platform) &&
    (!/Safari/.test(userAgent) || /CriOS|FxiOS|OPiOS|mercury/.test(userAgent));

  // Check for Android web view (common patterns)
  const isAndroidWebView = /Android/.test(userAgent) && /wv/.test(userAgent);

  // Check for mobile app indicators
  const isMobileApp = (/Greenback|wv|WebView|Version\/|wv\//.test(userAgent)) ||
    ((window as any).ReactNativeWebView !== undefined) ||
    ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined);

  return {
    isWebView: isIOSWebView || isAndroidWebView || isMobileApp,
    isIOSWebView,
    isAndroidWebView,
    isMobileApp,
    platform: isIOSWebView ? 'ios' : isAndroidWebView ? 'android' : undefined
  };
};

/**
 * Attempts to close the current web view and return to the mobile app
 */
export const closeWebView = (): boolean => {
  const webViewInfo = detectWebView();

  try {
    if (webViewInfo.isIOSWebView && (window as any).webkit?.messageHandlers?.closeWebView) {
      // iOS native app with message handler
      (window as any).webkit.messageHandlers.closeWebView.postMessage({});
      return true;
    } else if (webViewInfo.isAndroidWebView && (window as any).Android?.closeWebView) {
      // Android native app with interface
      (window as any).Android.closeWebView();
      return true;
    } else if ((window as any).ReactNativeWebView?.postMessage) {
      // React Native WebView
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({ action: 'close' }));
      return true;
    }
  } catch (error) {
    console.warn('Failed to close web view:', error);
  }

  return false;
};

/**
 * Sends authentication tokens from the web app to the native mobile app
 * via the React Native WebView postMessage bridge.
 * Returns true if the message was sent successfully.
 */
export const sendTokensToNative = (
  accessToken: string,
  refreshToken: string | undefined,
  email: string | undefined,
  userUuid: string | undefined,
  subscriptionActive?: boolean,
  betaWaitlist?: boolean,
): boolean => {
  try {
    if ((window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({
        action: 'auth_success',
        access_token: accessToken,
        refresh_token: refreshToken,
        email: email,
        user_uuid: userUuid,
        subscription_active: subscriptionActive,
        beta_waitlist: betaWaitlist,
      }));
      return true;
    }
  } catch (error) {
    console.warn('Failed to send tokens to native app:', error);
  }
  return false;
};

/**
 * Redirects the browser back to the native mobile app via deep link after auth.
 * Used when the app opened this page with a `return_scheme` query parameter
 * (external browser flow, Apple compliance).
 */
export const redirectToMobileApp = (
  scheme: string,
  accessToken: string,
  refreshToken: string | undefined,
  email: string | undefined,
  userUuid: string | undefined,
): void => {
  const params = new URLSearchParams();
  params.set("access_token", accessToken);
  if (refreshToken) params.set("refresh_token", refreshToken);
  if (email) params.set("email", email);
  if (userUuid) params.set("user_uuid", userUuid);
  window.location.href = `${scheme}://auth-callback?${params.toString()}`;
};

/**
 * Gets mobile app friendly viewport settings
 */
export const getMobileViewportSettings = () => {
  const webViewInfo = detectWebView();

  if (webViewInfo.isWebView) {
    return {
      width: 'device-width',
      initialScale: 1,
      maximumScale: 1,
      userScalable: 'no',
      viewportFit: 'cover'
    };
  }

  return {
    width: 'device-width',
    initialScale: 1
  };
};
